import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Bell, Shield, HelpCircle, LogOut, Trash2, BookOpen, Video, Users, Award, CheckCircle, Pencil, Camera, Check, X, Clock } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useVerification } from '../../hooks/useVerification';
import VerifyModal from '../common/VerifyModal';
import BadgeIcon from '../common/BadgeIcon';
import { supabase } from '../../lib/supabase';
import { roleLabels, ROLE_OPTIONS, isDspRole, isFamilyRole } from '../../lib/roleLabels';
import TeamTag from '../common/TeamTag';
import { INTEREST_OPTIONS } from '../../lib/interests';
import { ZIP_NOTICE, cleanZip, isValidZip } from '../../lib/zipCode';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

// Everything here is unbuilt. These used to be two separate lists rendered as
// live rows — testers tapped them and got nothing, and "Partner Benefits" even
// carried a "New" badge for a feature that does not exist.
//
// They now sit in ONE card under a single "Coming soon" heading rather than
// eight individual Soon pills: the per-row treatment was honest but read as an
// inventory of everything missing, which is a worse first impression than the
// truth warrants. One quiet label says the same thing once.
//
// When a row ships, move it OUT of this list into its own card with a real
// onClick — don't just delete the flag.
const comingSoonItems = [
  { icon: Video, label: 'Saved Videos' },
  { icon: Users, label: 'Find a Mentor' },
  { icon: Award, label: 'Partner Benefits' },
  { icon: BookOpen, label: 'My Bookshelf' },
  { icon: Bell, label: 'Notifications' },
  { icon: Shield, label: 'Privacy & Safety' },
  { icon: Settings, label: 'Account Settings' },
  { icon: HelpCircle, label: 'Help & Feedback' },
];


// An inert row: not a button, no chevron (which implies navigation), no hover.
// The card heading carries the "coming soon" message for the whole group.
const ComingSoonRow = ({ item, isLast }) => {
  const Icon = item.icon;
  return (
    <div
      className="w-full px-4 py-3 flex items-center gap-3"
      style={{ borderBottom: isLast ? 'none' : '1px solid var(--card-border)' }}
    >
      <Icon size={18} style={{ color: 'var(--text-tertiary)', flexShrink: 0, opacity: 0.7 }} />
      <span className="font-medium text-sm truncate" style={{ color: 'var(--text-tertiary)' }}>
        {item.label}
      </span>
    </div>
  );
};

const ProfileScreen = () => {
  const navigate = useNavigate();
  const { userData, session, signOut, deleteAccount, updateProfile, refreshProfile } = useUser();
  const { showVerifyModal, setShowVerifyModal } = useVerification();

  // The gear had no onClick at all. Rather than a dead icon or a "Soon" pill
  // in a header, it now jumps to the Public Profile Privacy block — the first
  // settings further down that actually work (privacy toggles, and sign out /
  // delete account below them), skipping the unbuilt settings list.
  const settingsRef = useRef(null);
  const scrollToSettings = () => {
    settingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editAgency, setEditAgency] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editYears, setEditYears] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editUseUsername, setEditUseUsername] = useState(false);
  const [editInterests, setEditInterests] = useState([]);
  const [editZip, setEditZip] = useState('');
  const [zipConfirming, setZipConfirming] = useState(false);
  const [saveError, setSaveError] = useState('');
  // 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid_format' | 'unchanged'
  const [usernameStatus, setUsernameStatus] = useState('idle');
  const [usernameError, setUsernameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [badgesData, setBadgesData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [gamificationLoading, setGamificationLoading] = useState(true);
  const [activeBadgeId, setActiveBadgeId] = useState(null);

  const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB

  // Gamification data — badges + stats from real DB
  useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    (async () => {
      const [badgesResult, statsResult] = await Promise.all([
        supabase.rpc('get_user_badges', { p_user_id: session.user.id }),
        supabase.rpc('get_user_stats',  { p_user_id: session.user.id }),
      ]);
      if (cancelled) return;
      if (!badgesResult.error) setBadgesData(badgesResult.data);
      if (!statsResult.error)  setStatsData(statsResult.data?.[0] ?? null);
      setGamificationLoading(false);
    })();
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  // Debounced username availability check — skips if value unchanged from saved
  useEffect(() => {
    if (!isEditing) return;
    if (editUsername === (userData.username || '')) { setUsernameStatus('unchanged'); return; }
    if (editUsername.length < 3) { setUsernameStatus('idle'); return; }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc('check_username_available', { p_username: editUsername });
      setUsernameStatus(data ?? 'taken');
    }, 500);
    return () => clearTimeout(t);
  }, [editUsername, isEditing, userData.username]);

  const handleUsernameChange = (val) => {
    setEditUsername(val.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 30));
    setUsernameError('');
    setUsernameStatus('idle');
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    const { error } = await deleteAccount();
    if (error) {
      setDeleteError('Something went wrong. Please try again.');
      setDeleting(false);
    } else {
      navigate('/welcome', { replace: true });
    }
  };

  const handleEditStart = () => {
    setEditName(userData.name || '');
    setEditRole(userData.role || '');
    setEditAgency(userData.agency || '');
    setEditBio(userData.bio || '');
    setEditYears(userData.years_experience != null ? String(userData.years_experience) : '');
    setEditUsername(userData.username || '');
    setEditUseUsername(userData.use_username ?? false);
    setEditInterests(userData.interests || []);
    setEditZip(userData.zip_code || '');
    setUsernameStatus('idle');
    setUsernameError('');
    setIsEditing(true);
  };

  const handleCancel = () => { setIsEditing(false); setSaveError(''); };

  // A zip saved before 054 was never typed beside the opt-in notice, so an
  // ordinary profile save doesn't count it — the member confirms it here.
  const handleConfirmZip = async () => {
    setZipConfirming(true);
    const { error } = await supabase.rpc('set_my_zip_code', { p_zip: userData.zip_code });
    if (error) setSaveError("Couldn't save your zip code. Please try again.");
    else await refreshProfile();
    setZipConfirming(false);
  };

  const handleSave = async () => {
    if (editZip !== '' && !isValidZip(editZip)) {
      setSaveError('Zip code must be 5 digits, or leave it blank.');
      return;
    }
    setSaving(true);
    setUsernameError('');
    setSaveError('');

    // 054: the zip only ever goes through set_my_zip_code, and only when it
    // changed — typing it beside ZIP_NOTICE is the opt-in, clearing it withdraws.
    const zipChanged = editZip !== (userData.zip_code || '');
    if (zipChanged) {
      const { error: zipError } = await supabase.rpc('set_my_zip_code', { p_zip: editZip });
      if (zipError) {
        setSaveError("Couldn't save your zip code. Please try again.");
        setSaving(false);
        return;
      }
    }

    // Handle username change via the rate-limited RPC
    const usernameChanged = editUsername !== (userData.username || '');
    if (usernameChanged && editUsername.length >= 3) {
      const { data: result } = await supabase.rpc('update_username', { p_username: editUsername });
      if (result === 'rate_limited') {
        setUsernameError('You can change your username once every 30 days.');
        setSaving(false);
        return;
      }
      if (result === 'taken') {
        setUsernameError('That username is already taken.');
        setSaving(false);
        return;
      }
      if (result === 'invalid') {
        setUsernameError("Username doesn't meet requirements.");
        setSaving(false);
        return;
      }
      // 'ok' — update local state immediately
      // (updateProfile won't write username since it's not in ALLOWED_PROFILE_FIELDS)
    }

    const { error: profileError } = await updateProfile({
      name: editName,
      role: editRole,
      agency: editAgency,
      bio: editBio,
      interests: editInterests,
      years_experience: editYears !== '' ? (parseInt(editYears, 10) || null) : null,
      use_username: editUseUsername,
    });

    if (profileError) {
      setSaveError('Failed to save changes. Please try again.');
      setSaving(false);
      return;
    }

    // Re-fetch profile so username (and any other DB-side changes) reflect in local state
    if (usernameChanged || zipChanged) await refreshProfile();

    // Re-fetch badges + stats so earned state reflects the just-saved profile
    const [badgesResult, statsResult] = await Promise.all([
      supabase.rpc('get_user_badges', { p_user_id: session.user.id }),
      supabase.rpc('get_user_stats',  { p_user_id: session.user.id }),
    ]);
    if (!badgesResult.error) setBadgesData(badgesResult.data);
    if (!statsResult.error)  setStatsData(statsResult.data?.[0] ?? null);

    setSaving(false);
    setIsEditing(false);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarError('');
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setAvatarError('Only JPEG, PNG, or WebP images are allowed.');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      setAvatarError('Image must be under 5MB.');
      return;
    }
    setAvatarUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${session.user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    if (!error) {
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      await updateProfile({ avatar_url: data.publicUrl });
    }
    setAvatarUploading(false);
  };

  const roleDisplay = roleLabels[userData.role] || null;
  const yearsDisplay = userData.years_experience != null
    ? `${userData.years_experience} ${userData.years_experience === 1 ? 'year' : 'years'}`
    : null;
  const subtitleParts = [roleDisplay, yearsDisplay, userData.agency].filter(Boolean);
  const subtitle = subtitleParts.join(' · ');

  const inputStyle = {
    ...cardStyle,
    color: 'var(--text-primary)',
    outline: 'none',
    width: '100%',
  };

  return (
    <div className="min-h-screen pb-nav">
      {/* Header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white tracking-tight">Profile</h1>
          <button
            onClick={scrollToSettings}
            aria-label="Go to settings"
            className="p-1.5 rounded-lg transition-all hover:opacity-80 active:scale-95"
          >
            <Settings size={22} className="text-white" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Profile Hero */}
        <div className="rounded-xl overflow-hidden" style={cardStyle}>
          <div
            className="relative h-28"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 50%, #4DAAAA 100%)' }}
          >
            <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)' }} />
            {!isEditing && (
              <button
                onClick={handleEditStart}
                aria-label="Edit profile"
                className="absolute top-3 right-3 p-1.5 rounded-lg transition-all active:scale-95"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}
              >
                <Pencil size={15} className="text-white" />
              </button>
            )}
          </div>
          <div className="px-5 pb-5 -mt-12 relative">
            {/* Avatar — stacked above name, overlaps gradient */}
            <div className="relative w-24 mb-3">
              {isEditing ? (
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                  {avatarUploading ? (
                    <div
                      className="w-24 h-24 rounded-full border-4 shadow-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--card-border)', borderColor: 'var(--card-bg)' }}
                    >
                      <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }} />
                    </div>
                  ) : userData.avatar_url ? (
                    <div className="relative">
                      <img
                        src={userData.avatar_url}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-4 shadow-lg"
                        style={{ borderColor: 'var(--card-bg)' }}
                      />
                      <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
                        <Camera size={22} className="text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <div
                        className="w-24 h-24 rounded-full border-4 shadow-lg flex items-center justify-center"
                        style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--card-bg)' }}
                      >
                        <span className="text-3xl font-bold text-white">
                          {userData.name ? userData.name.charAt(0).toUpperCase() : '?'}
                        </span>
                      </div>
                      <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
                        <Camera size={22} className="text-white" />
                      </div>
                    </div>
                  )}
                  <div
                    className="absolute bottom-1 right-1 w-6 h-6 rounded-full border-2 flex items-center justify-center"
                    style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--card-bg)' }}
                  >
                    <Camera size={11} className="text-white" />
                  </div>
                </label>
              ) : (
                <>
                  {userData.avatar_url ? (
                    <img
                      src={userData.avatar_url}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-4 shadow-lg"
                      style={{ borderColor: 'var(--card-bg)' }}
                    />
                  ) : (
                    <div
                      className="w-24 h-24 rounded-full border-4 shadow-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--card-bg)' }}
                    >
                      <span className="text-3xl font-bold text-white">
                        {userData.name ? userData.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                  )}
                  <div
                    className="absolute bottom-1 right-1 w-5 h-5 rounded-full border-2"
                    style={{ backgroundColor: 'var(--color-success)', borderColor: 'var(--card-bg)' }}
                  />
                </>
              )}
            </div>
            {isEditing && avatarError && (
              <p className="text-xs mb-2 -mt-1" style={{ color: 'var(--color-error)' }}>{avatarError}</p>
            )}

            {/* Name / role / agency — clearly in white card area */}
            {isEditing ? (
              <input
                className="rounded-lg px-3 py-2 text-base font-bold mb-1"
                style={inputStyle}
                value={editName}
                onChange={e => setEditName(e.target.value)}
                placeholder="Your name"
              />
            ) : (
              <div className="flex items-center gap-2 min-w-0 mb-0.5">
                <h2 className="text-xl font-bold text-primary truncate">{userData.name || 'DSP Member'}</h2>
                <TeamTag userId={session?.user?.id} />
                {/* 048: "Verified DSP" only for DSP roles. A verified non-DSP can
                    post like anyone verified; there is just no DSP label.
                    Unverified always says so; a non-DSP's tap explains why
                    there is nothing to verify (VerifyModal). */}
                {userData?.verified ? (isDspRole(userData.role) && (
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(var(--brand-primary-rgb, 91, 140, 111), 0.15)' }}>
                    <CheckCircle size={14} style={{ color: 'var(--brand-primary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--brand-primary)' }}>Verified DSP</span>
                  </div>
                )) : userData?.verification_status === 'pending' ? (
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}>
                    <Clock size={14} style={{ color: 'var(--text-tertiary)' }} aria-hidden="true" />
                    <span className="text-xs font-medium text-tertiary">Verification pending</span>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowVerifyModal(true)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full transition-all hover:brightness-110 active:scale-[0.98] flex-shrink-0"
                    style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
                  >
                    <Shield size={14} style={{ color: 'var(--text-tertiary)' }} aria-hidden="true" />
                    <span className="text-xs font-medium text-tertiary">Not verified</span>
                    {isDspRole(userData?.role) && (
                      <span className="text-xs font-semibold" style={{ color: 'var(--brand-primary)' }}>· Verify</span>
                    )}
                  </button>
                )}
              </div>
            )}
            {!isEditing && subtitle && <p className="text-sm text-tertiary mb-0.5">{subtitle}</p>}
            {!isEditing && userData.username && (
              <p className="text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>@{userData.username}</p>
            )}
            {/* Tappable ONLY on your own profile — you can always browse your
                own lists (028). PublicProfileScreen keeps these as plain text:
                a tappable count there would imply a list nobody can open. */}
            {!isEditing && statsData && (
              <p className="text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>
                <button
                  onClick={() => navigate('/connections?tab=followers')}
                  className="transition-all hover:brightness-110 active:scale-[0.98]"
                >
                  <span className="font-semibold text-secondary">{statsData.follower_count ?? 0}</span>
                  {' '}<span className="underline decoration-dotted underline-offset-2">Followers</span>
                </button>
                <span className="mx-1.5">·</span>
                <button
                  onClick={() => navigate('/connections?tab=following')}
                  className="transition-all hover:brightness-110 active:scale-[0.98]"
                >
                  <span className="font-semibold text-secondary">{statsData.following_count ?? 0}</span>
                  {' '}<span className="underline decoration-dotted underline-offset-2">Following</span>
                </button>
              </p>
            )}
            {!isEditing && userData.bio && (
              <p className="text-sm leading-relaxed mt-1 mb-0.5" style={{ color: 'var(--text-secondary)' }}>{userData.bio}</p>
            )}

            {/* Edit mode fields */}
            {isEditing && (
              <div className="mt-3 space-y-3">
                {/* Username */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">Username</p>
                  <div className="relative flex items-center">
                    <span className="absolute left-3 text-sm font-medium select-none" style={{ color: 'var(--text-tertiary)' }}>@</span>
                    <input
                      className="rounded-lg pl-7 pr-8 py-2 text-sm"
                      style={{
                        ...inputStyle,
                        border: `1px solid ${
                          usernameStatus === 'available' ? 'rgba(74,222,128,0.5)'
                          : (usernameStatus === 'taken' || usernameStatus === 'reserved' || usernameStatus === 'invalid_format') ? 'rgba(239,68,68,0.5)'
                          : 'var(--card-border)'
                        }`,
                      }}
                      value={editUsername}
                      onChange={e => handleUsernameChange(e.target.value)}
                      placeholder="yourhandle"
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                    />
                    <div className="absolute right-3">
                      {usernameStatus === 'checking' && (
                        <span className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin block" />
                      )}
                      {usernameStatus === 'available' && <Check size={14} className="text-green-500" />}
                      {(usernameStatus === 'taken' || usernameStatus === 'reserved' || usernameStatus === 'invalid_format') && (
                        <X size={14} className="text-red-500" />
                      )}
                    </div>
                  </div>
                  {usernameStatus === 'available' && (
                    <p className="text-xs mt-1" style={{ color: 'var(--color-success, #4ade80)' }}>Available!</p>
                  )}
                  {usernameStatus === 'taken' && <p className="text-xs mt-1 text-red-500">Already taken.</p>}
                  {usernameStatus === 'reserved' && <p className="text-xs mt-1 text-red-500">That name is reserved.</p>}
                  {usernameError && <p className="text-xs mt-1 text-red-500">{usernameError}</p>}
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    Letters, numbers, underscores · 3–30 chars · Changes limited to once per 30 days
                  </p>
                </div>

                {/* Display preference */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">Appear publicly as</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditUseUsername(false)}
                      className="flex-1 py-2 rounded-lg text-xs text-center transition-all active:scale-[0.98]"
                      style={!editUseUsername ? {
                        border: '2px solid var(--brand-primary)',
                        backgroundColor: 'rgba(var(--brand-primary-rgb, 91, 140, 111), 0.12)',
                        color: 'var(--brand-primary)',
                        fontWeight: 600,
                      } : {
                        ...cardStyle,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      Real name
                    </button>
                    <button
                      onClick={() => setEditUseUsername(true)}
                      className="flex-1 py-2 rounded-lg text-xs text-center transition-all active:scale-[0.98]"
                      style={editUseUsername ? {
                        border: '2px solid var(--brand-primary)',
                        backgroundColor: 'rgba(var(--brand-primary-rgb, 91, 140, 111), 0.12)',
                        color: 'var(--brand-primary)',
                        fontWeight: 600,
                      } : {
                        ...cardStyle,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      @username
                    </button>
                  </div>
                </div>

                {/* Role — one list for the whole app (lib/roleLabels), grouped so
                    eight options stay scannable. */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">Role</p>
                  {[
                    { heading: null, options: ROLE_OPTIONS.filter(r => r.dsp), cols: 'grid-cols-2 sm:grid-cols-4' },
                    { heading: 'Not a DSP', options: ROLE_OPTIONS.filter(r => !r.dsp), cols: 'grid-cols-2 sm:grid-cols-4' },
                  ].map(group => (
                    <div key={group.heading || 'dsp'} className={group.heading ? 'mt-2' : ''}>
                      {group.heading && <p className="text-[11px] text-tertiary mb-1">{group.heading}</p>}
                      <div className={`grid ${group.cols} gap-2`}>
                        {group.options.map(({ id, label }) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setEditRole(id)}
                            className="py-2 px-1 rounded-lg text-xs text-center leading-tight transition-all"
                            style={editRole === id ? {
                              border: '2px solid var(--brand-primary)',
                              backgroundColor: 'rgba(var(--brand-primary-rgb, 91, 140, 111), 0.12)',
                              color: 'var(--brand-primary)',
                              fontWeight: 600,
                            } : {
                              ...cardStyle,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Agency — for family roles the same column holds where they
                    live (onboarding asks "Where are you located?"), and 056
                    keeps it off posts and their public profile. */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">
                    {isFamilyRole(editRole) ? 'Location (not shown publicly)' : 'Agency / Organization'}
                  </p>
                  <input
                    className="rounded-lg px-3 py-2 text-sm"
                    style={inputStyle}
                    value={editAgency}
                    onChange={e => setEditAgency(e.target.value)}
                    placeholder={isFamilyRole(editRole) ? 'City, State (optional)' : 'Agency or organization (optional)'}
                  />
                </div>

                {/* Zip — opt-in, for counting DSPs by area (054). Shown for DSP
                    roles, and to anyone who already has one so they can remove it. */}
                {(isDspRole(editRole) || userData.zip_code) && (
                  <div>
                    <label htmlFor="profile-zip" className="block text-xs text-tertiary mb-1.5 font-medium">
                      Zip code <span className="font-normal">(optional, never shown)</span>
                    </label>
                    <input
                      id="profile-zip"
                      className="rounded-lg px-3 py-2 text-sm"
                      style={inputStyle}
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={5}
                      value={editZip}
                      onChange={e => setEditZip(cleanZip(e.target.value))}
                      placeholder="5-digit zip"
                    />
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
                      {ZIP_NOTICE}
                    </p>
                    {userData.zip_code && !userData.location_consent_at && editZip === userData.zip_code && (
                      <div className="mt-2 rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-2" style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)' }}>
                        <span className="flex-1 min-w-[12rem]">You added this before we started counting, so it isn't counted yet.</span>
                        <button
                          type="button"
                          onClick={handleConfirmZip}
                          disabled={zipConfirming}
                          className="px-3 py-1 rounded-full font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
                          style={{ backgroundColor: 'var(--brand-primary)' }}
                        >
                          {zipConfirming ? 'Saving…' : 'Count my zip'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Bio */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">Bio</p>
                  <textarea
                    className="rounded-lg px-3 py-2 text-sm resize-none"
                    style={{ ...inputStyle, minHeight: '4.5rem' }}
                    value={editBio}
                    onChange={e => setEditBio(e.target.value)}
                    placeholder="A short bio (optional)"
                    maxLength={500}
                  />
                  <p className="text-xs mt-1 text-right" style={{ color: 'var(--text-tertiary)' }}>
                    {editBio.length}/500
                  </p>
                </div>

                {/* Interests */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">Interests</p>
                  <div className="grid grid-cols-2 gap-2">
                    {INTEREST_OPTIONS.map(opt => {
                      const selected = editInterests.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setEditInterests(
                            selected
                              ? editInterests.filter(i => i !== opt.id)
                              : [...editInterests, opt.id]
                          )}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-left transition-all"
                          style={selected ? {
                            border: '2px solid var(--brand-primary)',
                            backgroundColor: 'rgba(var(--brand-primary-rgb, 91, 140, 111), 0.12)',
                            color: 'var(--brand-primary)',
                            fontWeight: 600,
                          } : {
                            ...cardStyle,
                            color: 'var(--text-secondary)',
                          }}
                        >
                          <span>{opt.emoji}</span>
                          <span className="leading-tight">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Years of experience */}
                <div>
                  <p className="text-xs text-tertiary mb-1.5 font-medium">Years of experience</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      className="rounded-lg px-3 py-2 text-sm text-center"
                      style={{ ...inputStyle, width: '5rem' }}
                      value={editYears}
                      onChange={e => setEditYears(e.target.value)}
                      placeholder="0"
                    />
                    <span className="text-sm text-tertiary">years</span>
                  </div>
                </div>

                {/* Save / Cancel */}
                {saveError && (
                  <p className="text-xs text-center" style={{ color: 'var(--color-error)' }}>{saveError}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--brand-primary)', color: 'white' }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="flex-1 py-2 rounded-lg text-sm font-medium transition-all active:scale-[0.98]"
                    style={cardStyle}
                  >
                    <span style={{ color: 'var(--text-secondary)' }}>Cancel</span>
                  </button>
                </div>
              </div>
            )}

            {/* Stats. A "12-day streak" callout sat above these as a hardcoded
                placeholder with nothing behind it; removed 2026-09-24 until
                streaks are designed (HANDOFF Open Decisions). */}
            {!isEditing && (
              <>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[
                    { value: gamificationLoading || !statsData ? '—' : String(statsData.post_count),       label: 'Posts',         color: 'var(--brand-primary)' },
                    { value: gamificationLoading || !statsData ? '—' : String(statsData.tables_joined),    label: 'Tables Joined', color: '#9B7EC8' },
                    { value: gamificationLoading || !statsData ? '—' : String(statsData.helpful_received), label: 'Helpful Votes', color: '#4DAAAA' },
                  ].map(stat => (
                    <div
                      key={stat.label}
                      className="text-center py-2.5 rounded-lg"
                      style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
                    >
                      <div className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                      <div className="text-xs text-tertiary">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="rounded-xl p-4" style={cardStyle}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-primary">Badges</h3>
            {!gamificationLoading && badgesData && (
              <span className="text-xs text-tertiary">
                {badgesData.filter(b => b.earned).length}/{badgesData.length} earned
              </span>
            )}
          </div>
          {gamificationLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-16 rounded-lg animate-pulse"
                  style={{ backgroundColor: 'var(--pill-bg)' }}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {(badgesData ?? []).map(badge => (
                <button
                  key={badge.id}
                  onClick={() => setActiveBadgeId(activeBadgeId === badge.id ? null : badge.id)}
                  className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'var(--pill-bg)',
                    opacity: badge.earned ? 1 : 0.4,
                  }}
                >
                  <BadgeIcon badge={badge} />
                  <span className="text-xs text-tertiary font-medium text-center leading-tight px-1">
                    {badge.label}
                  </span>
                  {activeBadgeId === badge.id && (
                    <span className="text-xs text-center leading-tight px-2 pb-1" style={{ color: 'var(--text-tertiary)' }}>
                      {badge.earned ? badge.description : badge.how_to_earn}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>


        {/* Coming soon — one heading for the whole group, not a badge per row */}
        <div className="rounded-xl overflow-hidden" style={cardStyle}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--card-border)' }}>
            <h3 className="font-semibold text-sm text-primary">Coming soon</h3>
            <p className="text-xs text-tertiary mt-0.5">Features we're building next.</p>
          </div>
          {comingSoonItems.map((item, i) => (
            <ComingSoonRow key={i} item={item} isLast={i === comingSoonItems.length - 1} />
          ))}
        </div>

        {/* Privacy — the gear's target: the first settings block that actually
            works, rather than the unbuilt list above it. */}
        <div ref={settingsRef} className="rounded-xl overflow-hidden scroll-mt-20" style={cardStyle}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--card-border)' }}>
            <h3 className="font-semibold text-sm text-primary">Public Profile Privacy</h3>
            <p className="text-xs text-tertiary mt-0.5">Your name and verified status are always visible.</p>
          </div>
          {/* Family roles store a location, not an agency, and 056 never shows
              it to anyone else — so the toggle would do nothing for them. */}
          {[
            !isFamilyRole(userData.role) && { key: 'show_agency', label: 'Agency / Organization' },
            { key: 'show_bio', label: 'Bio' },
            { key: 'show_interests', label: 'Interests' },
          ].filter(Boolean).map(({ key, label }, i, arr) => {
            const isOn = userData[key] ?? true;
            return (
              <button
                key={key}
                onClick={() => updateProfile({ [key]: !isOn })}
                className="w-full px-4 py-3.5 flex items-center justify-between transition-all hover-surface"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--card-border)' : 'none' }}
              >
                <span className="text-sm font-medium text-primary">{label}</span>
                <div
                  className="relative w-11 h-6 rounded-full transition-all duration-200 flex-shrink-0"
                  style={{ backgroundColor: isOn ? 'var(--brand-primary)' : 'var(--card-border)' }}
                >
                  <div
                    className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all duration-200"
                    style={{ left: isOn ? '1.25rem' : '0.125rem' }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          className="w-full py-3 rounded-xl text-sm font-medium transition-all active:scale-[0.98]"
          style={{
            backgroundColor: 'var(--pill-bg)',
            color: 'var(--color-error)',
            border: '1px solid var(--pill-border)',
          }}
        >
          <div className="flex items-center justify-center gap-2">
            <LogOut size={16} />
            Sign Out
          </div>
        </button>

        {/* Delete Account */}
        {!showDeleteConfirm ? (
          <button
            onClick={() => { setShowDeleteConfirm(true); setDeleteError(null); }}
            className="w-full py-3 rounded-xl text-sm font-medium transition-all active:scale-[0.98]"
            style={{
              backgroundColor: 'var(--pill-bg)',
              color: 'var(--color-error)',
              border: '1px solid var(--pill-border)',
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <Trash2 size={16} />
              Delete Account
            </div>
          </button>
        ) : (
          <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
            <p className="text-sm font-medium text-center" style={{ color: '#991B1B' }}>
              Permanently delete your account? This cannot be undone.
            </p>
            {deleteError && (
              <p className="text-xs text-center" style={{ color: 'var(--color-error)' }}>{deleteError}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: '#DC2626', color: '#fff' }}
              >
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={deleting}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)', border: '1px solid var(--pill-border)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-tertiary pb-2">DSP Cafe v1.0.0</p>
      </div>

      {showVerifyModal && (
        <VerifyModal onClose={() => setShowVerifyModal(false)} />
      )}

    </div>
  );
};

export default ProfileScreen;
